export const JOB_COSTING_MODE = {
    EXPORT: "export",
    IMPORT: "import",
};

/** Detect export vs import from route path or ?mode=import (legacy /import-job-costing supported). */
export function getJobCostingMode(pathname = "", search = "") {
    if (pathname.includes("import-job-costing")) {
        return JOB_COSTING_MODE.IMPORT;
    }
    const params = new URLSearchParams(search);
    if (params.get("mode") === "import") {
        return JOB_COSTING_MODE.IMPORT;
    }
    return JOB_COSTING_MODE.EXPORT;
}

export function normalizeARCharge(item = {}) {
    return {
        id: item.id ?? item.ID ?? item.rowid ?? 0,
        chargeCode: item.chargeCode ?? item.CHARGE_CODE ?? "",
        description: item.description ?? item.DESCRIPTION ?? "",
        currency: item.currency ?? item.CURRENCY ?? "USD",
        roe: item.roe ?? item.ROE ?? "1",
        basis: item.basis ?? item.BASIS ?? "",
        container: item.container ?? item.CONTAINER ?? "",
        quantity: item.quantity ?? item.QUANTITY ?? "1",
        billToParty: item.billToParty ?? item.BILL_TO_PARTY ?? "",
        billToPartyId: item.billToPartyId ?? item.BILL_TO_PARTY_ID ?? 0,
        billToPartyRef: item.billToPartyRef ?? item.BILL_TO_PARTY_REF ?? "",
        amount: item.amount ?? item.AMOUNT ?? "0",
        sellRate: item.sellRate ?? item.SELL_RATE ?? 0,
        ppcc: item.ppcc ?? item.PPCC ?? "P",
        invoiceStatus: item.invoiceStatus ?? item.INVOICE_STATUS ?? "",
        invoiceId: item.invoiceId ?? item.INVOICE_ID ?? null,
        invoiceNo: item.invoiceNo ?? item.INVOICE_NO ?? "",
        paymentStatus: item.paymentStatus ?? item.PAYMENT_STATUS ?? "",
        isActive: item.isActive ?? item.IS_ACTIVE ?? true,
    };
}

export function normalizeAPCharge(item = {}) {
    return {
        id: item.id ?? item.ID ?? item.rowid ?? 0,
        chargeCode: item.chargeCode ?? item.CHARGE_CODE ?? "",
        description: item.description ?? item.DESCRIPTION ?? "",
        currency: item.currency ?? item.CURRENCY ?? "USD",
        roe: item.roe ?? item.ROE ?? "1",
        basis: item.basis ?? item.BASIS ?? "",
        container: item.container ?? item.CONTAINER ?? "",
        quantity: item.quantity ?? item.QUANTITY ?? "1",
        vendor: item.vendor ?? item.VENDOR ?? "",
        vendorId: item.vendorId ?? item.VENDOR_ID ?? 0,
        vendorRef: item.vendorRef ?? item.VENDOR_REF ?? "",
        amount: item.amount ?? item.AMOUNT ?? "0",
        buyRate: item.buyRate ?? item.BUY_RATE ?? 0,
        ppcc: item.ppcc ?? item.PPCC ?? "P",
        billStatus: item.billStatus ?? item.BILL_STATUS ?? "",
        billId: item.billId ?? item.BILL_ID ?? null,
        billNo: item.billNo ?? item.BILL_NO ?? "",
        paymentStatus: item.paymentStatus ?? item.PAYMENT_STATUS ?? "",
        isActive: item.isActive ?? item.IS_ACTIVE ?? true,
    };
}

export function normalizeARCharges(charges = []) {
    return (Array.isArray(charges) ? charges : []).map(normalizeARCharge);
}

export function normalizeAPCharges(charges = []) {
    return (Array.isArray(charges) ? charges : []).map(normalizeAPCharge);
}

/** Add camelCase aliases on import booking payloads for shared UI. */
export function normalizeBookingData(raw, mode) {
    if (!raw) return null;
    if (mode !== JOB_COSTING_MODE.IMPORT) return raw;

    return {
        ...raw,
        fileNumber: raw.fileNumber || raw.FileRefNumber || raw.BookingNumber,
        bookingNumber: raw.bookingNumber || raw.BookingNumber,
        customerName: raw.customerName || raw.CustomerName,
        carrier: raw.carrier || raw.CarrierName,
        portOfLoading: raw.portOfLoading || raw.PortOfLoading,
        portOfLoadingCode: raw.portOfLoadingCode || raw.PortOfLoadingCode,
        portOfDischarge: raw.portOfDischarge || raw.PortOfDischarge,
        portOfDischargeCode: raw.portOfDischargeCode || raw.PortOfDischargeCode,
        etsAtPol: raw.etsAtPol || raw.ETSPOLDate,
    };
}

function parseFormSnapshot(data) {
    if (!data?.formSnapshot) return data;
    const snapshot =
        typeof data.formSnapshot === "string"
            ? JSON.parse(data.formSnapshot)
            : data.formSnapshot;
    return { ...data, ...snapshot };
}

/** Resolve booking record and charge arrays from API response. */
export function resolveBookingPayload(data, mode) {
    if (!data) {
        return { booking: null, arCharges: [], apCharges: [] };
    }

    const merged = parseFormSnapshot(data);

    if (mode === JOB_COSTING_MODE.IMPORT) {
        const arRaw = merged.BolArChargesData ?? data.BolArChargesData ?? [];
        const apRaw = merged.BolApChargesData ?? data.BolApChargesData ?? [];
        return {
            booking: normalizeBookingData(merged, mode),
            arCharges: normalizeARCharges(arRaw),
            apCharges: normalizeAPCharges(apRaw),
        };
    }

    const arRaw = merged.arCharges ?? data.arCharges ?? [];
    const apRaw = merged.apCharges ?? data.apCharges ?? [];
    return {
        booking: normalizeBookingData(merged, mode),
        arCharges: normalizeARCharges(arRaw),
        apCharges: normalizeAPCharges(apRaw),
    };
}

export function extractLinkedDocuments(arCharges = [], apCharges = []) {
    const invoices = new Map();
    const bills = new Map();

    arCharges.forEach((c) => {
        if (c.invoiceNo && c.invoiceId) {
            invoices.set(c.invoiceNo, {
                id: c.invoiceId,
                invoiceNo: c.invoiceNo,
                status: c.invoiceStatus,
            });
        }
    });

    apCharges.forEach((c) => {
        if (c.billNo && c.billId) {
            bills.set(c.billNo, {
                id: c.billId,
                billNo: c.billNo,
                status: c.billStatus,
            });
        }
    });

    return {
        linkedInvoices: Array.from(invoices.values()),
        linkedBills: Array.from(bills.values()),
    };
}
